import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { AlertCircle, ChevronLeft, Gift, ShoppingBag, Calendar, CalendarDays, Trophy, Phone, ChevronRight, Flame, RefreshCw, Sparkles, Tag } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { isPhoneVerified } from '../../lib/phoneVerification';
import { fetchVipRewardsStatus, vipRewardsCacheScopeForUser } from '../../services/vipRewards';
import MiniRouletteLogo from './MiniRouletteLogo';

const SHOW_PLAYER_TIER_SYSTEM = false;
const REFRESH_TIMEOUT_MS = 15000;

const formatFp = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return '0 FP';
  const text = Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace(/\.?0+$/, '');
  return `${text} FP`;
};

/**
 * Explore & Earn — hub page with tile grid linking to sub-pages.
 */
export default function ExploreEarnPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const [refreshVersion, setRefreshVersion] = useState(0);
  const scope = `${tenantSlug || ''}:${user?.tenant_id || ''}:${user?.id || ''}:${refreshVersion}`;
  const [snapshot, setSnapshot] = useState({ scope: '', status: 'loading' });
  const currentSnapshot = snapshot.scope === scope && snapshot.status === 'ready' ? snapshot : null;
  const isLoading = snapshot.scope !== scope || snapshot.status === 'loading';
  const loadError = snapshot.scope === scope && snapshot.status === 'error';
  const verifyStatus = currentSnapshot?.verify || null;
  const vipStatus = currentSnapshot?.vip || null;
  const promoCodeSectionEnabled = currentSnapshot?.promo === true;
  const taskAvailability = currentSnapshot?.tasks || null;
  const currentRouletteStatus = currentSnapshot?.roulette || null;
  const authPhoneVerified = isPhoneVerified(user);
  const phoneVerified = authPhoneVerified || verifyStatus?.verified === true;
  const phoneRewardAmount = Number(verifyStatus?.phone_verify_reward_fp || 0);
  const phoneRewardEnabled = verifyStatus?.phone_verify_reward_enabled === true && phoneRewardAmount > 0;
  const phoneRewardLabel = formatFp(phoneRewardAmount);
  const showPhoneVerifyTile = !phoneVerified && phoneRewardEnabled;

  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const basePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';

  useEffect(() => {
    const refresh = () => setRefreshVersion((version) => version + 1);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    let active = true;
    let timedOut = false;
    const controller = new AbortController();
    const cacheScope = vipRewardsCacheScopeForUser(user);
    const getStatus = (path) => api.get(path, {
      signal: controller.signal,
      timeout: REFRESH_TIMEOUT_MS,
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    setSnapshot({ scope, status: 'loading' });
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
      if (active) setSnapshot({ scope, status: 'error' });
    }, REFRESH_TIMEOUT_MS);

    Promise.all([
      getStatus('/api/public/settings/promo-code'),
      getStatus('/api/tasks/availability'),
      getStatus('/api/roulette/my-spin-today'),
      fetchVipRewardsStatus({ ...cacheScope, preferCache: false }),
      authPhoneVerified
        ? Promise.resolve({ data: { verified: true } })
        : getStatus('/api/users/me/phone-verification/status'),
    ]).then(([promoResponse, tasksResponse, rouletteResponse, vipResponse, phoneResponse]) => {
      if (!active || timedOut) return;
      const tasks = tasksResponse.data;
      const phone = phoneResponse.data;
      if (
        typeof promoResponse.data?.enabled !== 'boolean'
        || typeof tasks?.daily_enabled !== 'boolean'
        || typeof tasks?.weekly_enabled !== 'boolean'
        || typeof rouletteResponse.data?.settings?.enabled !== 'boolean'
        || typeof rouletteResponse.data?.availability?.enabled !== 'boolean'
        || typeof vipResponse?.enabled !== 'boolean'
        || typeof phone?.verified !== 'boolean'
      ) {
        throw new Error('Incomplete Explore & Earn status');
      }
      setSnapshot({
        scope,
        status: 'ready',
        promo: promoResponse.data?.enabled === true,
        tasks: {
          daily_enabled: tasks.daily_enabled === true,
          weekly_enabled: tasks.weekly_enabled === true,
          daily_has_earned_rewards: tasks.daily_has_earned_rewards === true,
          weekly_has_earned_rewards: tasks.weekly_has_earned_rewards === true,
        },
        roulette: rouletteResponse.data,
        vip: vipResponse,
        verify: phone.phone_verify_reward_resolved === false
          ? { verified: phone.verified === true, phone_verify_reward_enabled: false }
          : phone,
      });
    }).catch(() => {
      if (active && !timedOut) {
        controller.abort();
        setSnapshot({ scope, status: 'error' });
      }
    }).finally(() => window.clearTimeout(timeoutId));

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [scope, authPhoneVerified]);

  const vipTile = vipStatus?.enabled
    ? [{
        key: 'vip-rewards',
        label: 'VIP Rewards',
        sub: Number(vipStatus?.claimable_count || 0) > 0
          ? `${vipStatus.claimable_count} reward ready to claim`
          : 'Freeplay bonus and cashback claims',
        icon: Sparkles,
        gradient: 'linear-gradient(135deg, #FACC15 0%, #D97706 100%)',
        iconColor: '#241600',
        ring: 'rgba(250,204,21,0.62)',
        hot: Number(vipStatus?.claimable_count || 0) > 0,
      }]
    : [];

  const tiles = [
    {
      key: 'promo-code',
      label: 'Redeem Promo Code',
      sub: 'Enter a code and claim rewards',
      icon: Tag,
      gradient: 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(168,85,247,0.58)',
    },
    {
      key: 'daily-tasks',
      label: 'Daily Tasks',
      sub: taskAvailability?.daily_has_earned_rewards
        ? 'Reward ready to claim'
        : 'Quick wins, every 24 h',
      icon: Calendar,
      gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(16,185,129,0.55)',
      hot: taskAvailability?.daily_has_earned_rewards,
    },
    {
      key: 'weekly-tasks',
      label: 'Weekly Tasks',
      sub: taskAvailability?.weekly_has_earned_rewards
        ? 'Reward ready to claim'
        : 'Bigger goals, bigger rewards',
      icon: CalendarDays,
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #B45309 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(245,158,11,0.55)',
      hot: taskAvailability?.weekly_has_earned_rewards,
    },
    ...vipTile,
    {
      key: 'roulette',
      label: 'Roulette',
      sub: 'Spin to win every day',
      // logo: spinning roulette wheel
      logo: 'roulette',
      gradient: 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(239,68,68,0.55)',
    },
    {
      key: 'marketplace',
      label: 'Marketplace',
      sub: 'Coming soon',
      icon: ShoppingBag,
      gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)',
      iconColor: '#3F2A00',
      ring: 'rgba(245,158,11,0.55)',
    },
    {
      key: 'tiers',
      label: 'VIP Tiers',
      sub: 'Climb the loyalty ladder',
      icon: Trophy,
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(139,92,246,0.55)',
    },
  ];
  const rouletteDisabled = !currentRouletteStatus || currentRouletteStatus?.settings?.enabled !== true || currentRouletteStatus?.availability?.enabled === false;
  const visibleTiles = (SHOW_PLAYER_TIER_SYSTEM ? tiles : tiles.filter(tile => tile.key !== 'tiers'))
    .filter(tile => !(tile.key === 'promo-code' && !promoCodeSectionEnabled))
    .filter(tile => !(tile.key === 'roulette' && rouletteDisabled))
    .filter(tile => !(
      tile.key === 'daily-tasks'
      && !taskAvailability?.daily_enabled
      && !taskAvailability?.daily_has_earned_rewards
    ))
    .filter(tile => !(
      tile.key === 'weekly-tasks'
      && !taskAvailability?.weekly_enabled
      && !taskAvailability?.weekly_has_earned_rewards
    ));

  return (
    <div
      className="player-app min-h-screen pb-12"
      style={{ color: '#FFFFFF' }}
      data-testid="explore-earn-page"
    >
      {/* Header — scrolls with content (matches home behavior) */}
      <div
        className="px-4 sm:px-6 py-4 flex items-center gap-3"
        style={{
          background: '#000000',
        }}
      >
        <button
          onClick={() => navigate(homePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid="explore-earn-back"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1
            className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2"
            style={{ color: '#FFFFFF' }}
          >
            <Gift className="w-6 h-6" style={{ color: '#FFD93D' }} />
            Explore &amp; Earn
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Complete tasks. Unlock rewards.
          </p>
        </div>
      </div>

      {/* Tile grid */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" aria-busy={isLoading}>
        {isLoading ? (
          <div className="min-h-[45vh] flex flex-col items-center justify-center text-center gap-3" role="status" data-testid="explore-refreshing">
            <RefreshCw className="w-7 h-7 motion-safe:animate-spin" style={{ color: '#FFD93D' }} aria-hidden="true" />
            <h2 className="display text-lg font-bold">Refreshing rewards…</h2>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.76)' }}>Checking your team’s latest offers.</p>
          </div>
        ) : loadError ? (
          <div className="min-h-[45vh] flex flex-col items-center justify-center text-center gap-3" role="alert" data-testid="explore-refresh-error">
            <AlertCircle className="w-7 h-7" style={{ color: '#FFD93D' }} aria-hidden="true" />
            <h2 className="display text-lg font-bold">Rewards couldn’t refresh</h2>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.76)' }}>Check your connection, then try again.</p>
            <button
              type="button"
              onClick={() => setRefreshVersion((version) => version + 1)}
              className="mt-2 min-h-11 rounded-xl px-5 font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-300"
              style={{ background: '#FFD93D', color: '#211900' }}
              data-testid="explore-refresh-retry"
            >
              Refresh again
            </button>
          </div>
        ) : (
          <>
        {/* Verify phone reward is tenant-controlled from Platform Rules. */}
        {showPhoneVerifyTile && (
          <button
            onClick={() => navigate(`${homePath}?open=phone-verify`)}
            className="w-full flex items-center gap-4 rounded-2xl px-4 sm:px-5 py-4 mb-5 transition active:scale-[0.99] hover:brightness-110 verify-phone-pulse"
            style={{
              background: 'linear-gradient(135deg, rgba(236,72,153,0.22) 0%, rgba(168,85,247,0.14) 100%)',
              border: '1px solid rgba(236,72,153,0.55)',
              boxShadow: '0 8px 22px rgba(236,72,153,0.25), inset 0 1px 0 rgba(255,255,255,0.08)',
            }}
            data-testid="explore-verify-phone-cta"
          >
            <span
              className="shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(180deg, #38BDF8, #2563EB)',
                boxShadow: '0 8px 18px rgba(56,189,248,0.55), inset 0 1px 0 rgba(255,255,255,0.35)',
              }}
              aria-hidden
            >
              <Phone className="w-8 h-8" style={{ color: '#FFFFFF' }} strokeWidth={2.6} />
            </span>
            <div className="flex-1 text-left min-w-0">
              <p className="display font-extrabold text-base sm:text-lg leading-tight" style={{ color: '#FFFFFF' }}>
                Verify your number — Earn {phoneRewardLabel}
              </p>
              <p className="text-xs sm:text-sm leading-tight mt-0.5" style={{ color: 'rgba(255,255,255,0.78)' }}>
                Add your phone to secure your account and instantly earn {phoneRewardLabel}
              </p>
            </div>
            <span
              className="display text-[10px] uppercase tracking-[0.18em] font-extrabold px-2.5 py-1 rounded-md shrink-0"
              style={{ background: '#FFD93D', color: '#3F2A00', boxShadow: '0 0 12px rgba(255,217,61,0.6)' }}
            >
              +{phoneRewardLabel}
            </span>
            <ChevronRight className="w-5 h-5 shrink-0" style={{ color: '#EC4899' }} />
          </button>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {visibleTiles.map((t) => {
            const Icon = t.icon;
            const isHot = !!t.hot;
            return (
              <button
                key={t.key}
                onClick={() => navigate(`${basePath}/${t.key}`)}
                className={`text-left rounded-2xl p-5 sm:p-6 transition active:scale-[0.99] hover:brightness-110 flex items-center gap-4 relative overflow-hidden ${isHot ? 'sm:col-span-2 explore-hot-tile' : ''}`}
                style={{
                  background: isHot
                    ? 'linear-gradient(135deg, rgba(255,107,0,0.18) 0%, rgba(255,26,26,0.10) 100%)'
                    : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${t.ring}`,
                  boxShadow: isHot
                    ? '0 0 0 1px rgba(255,107,0,0.45), 0 14px 36px rgba(255,107,0,0.28), inset 0 1px 0 rgba(255,255,255,0.10)'
                    : `0 8px 22px ${t.ring.replace('0.55', '0.18')}, inset 0 1px 0 rgba(255,255,255,0.05)`,
                }}
                data-testid={`explore-tile-${t.key}`}
              >
                {/* Animated flame backdrop on the hot/promoted tile */}
                {isHot && (
                  <>
                    <span aria-hidden className="explore-hot-flame explore-hot-flame-1" />
                    <span aria-hidden className="explore-hot-flame explore-hot-flame-2" />
                    <span aria-hidden className="explore-hot-flame explore-hot-flame-3" />
                    {/* "TRENDING" pill */}
                    <span
                      className="absolute top-2.5 right-2.5 display text-[10px] font-extrabold uppercase tracking-[0.16em] px-2 py-0.5 rounded-md flex items-center gap-1 explore-hot-pill"
                      style={{
                        background: 'linear-gradient(180deg, #FFD93D, #F97316)',
                        color: '#3F1A00',
                        boxShadow: '0 0 18px rgba(249,115,22,0.6)',
                      }}
                    >
                      <Flame className="w-3 h-3" strokeWidth={3} />
                      Trending
                    </span>
                  </>
                )}
                {t.logo === 'roulette' ? (
                  <span
                    className="shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{
                      background: t.gradient,
                      boxShadow: `0 8px 18px ${t.ring}, inset 0 1px 0 rgba(255,255,255,0.45)`,
                    }}
                    aria-hidden
                  >
                    <MiniRouletteLogo size={40} spin />
                  </span>
                ) : (
                  <span
                    className={`shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center relative ${t.key === 'marketplace' ? 'marketplace-bag-anim' : ''} ${isHot ? 'explore-hot-icon' : ''}`}
                    style={{
                      background: t.gradient,
                      boxShadow: isHot
                        ? '0 10px 26px rgba(255,107,0,0.55), 0 0 26px rgba(255,217,61,0.45), inset 0 1px 0 rgba(255,255,255,0.55)'
                        : `0 8px 18px ${t.ring}, inset 0 1px 0 rgba(255,255,255,0.45)`,
                    }}
                    aria-hidden
                  >
                    <Icon className="w-7 h-7 relative" style={{ color: t.iconColor }} strokeWidth={2.4} />
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <p
                    className="display font-extrabold text-base sm:text-lg leading-tight"
                    style={{ color: '#FFFFFF' }}
                  >
                    {t.label}
                  </p>
                  <p
                    className="text-xs sm:text-sm leading-tight mt-0.5"
                    style={{ color: 'rgba(255,255,255,0.72)' }}
                  >
                    {t.sub}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Logged-in chip */}
        <div className="mt-8 flex justify-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px]"
            style={{
              background: 'rgba(34,255,136,0.10)',
              border: '1px solid rgba(34,255,136,0.30)',
              color: '#86EFAC',
            }}
          >
            Logged in as {user?.username || user?.email || 'player'}
          </span>
        </div>
          </>
        )}
      </div>
    </div>
  );
}
